import React, { useState, useEffect } from 'react';
import { Input, Textarea, Select, Button } from '@/components/ui';
import type {
  Organization,
  SubscriptionTier,
} from '@/services/admin/AdminService';

interface OrganizationFormProps {
  organization?: Organization | null;
  onSubmit: (data: OrganizationFormData) => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface OrganizationFormData {
  name: string;
  description: string;
  subscription_tier?: SubscriptionTier;
  max_users?: number;
  max_projects?: number;
  is_active?: boolean;
}

const TIER_OPTIONS = [
  { value: 'FREE', label: 'Free' },
  { value: 'STARTER', label: 'Starter' },
  { value: 'PROFESSIONAL', label: 'Professional' },
  { value: 'ENTERPRISE', label: 'Enterprise' },
];

export const OrganizationForm: React.FC<OrganizationFormProps> = ({
  organization,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    description: '',
    subscription_tier: 'FREE',
    max_users: 5,
    max_projects: 10,
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name,
        description: organization.description || '',
        subscription_tier: organization.subscription_tier,
        max_users: organization.max_users,
        max_projects: organization.max_projects,
        is_active: organization.is_active,
      });
    }
  }, [organization]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    } else if (formData.name.length > 200) {
      newErrors.name = 'Name must be 200 characters or less';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be 1000 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  const handleChange = (
    field: keyof OrganizationFormData,
    value: string | number | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const isEditing = !!organization;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Organization Name"
        value={formData.name}
        onChange={(e) => handleChange('name', e.target.value)}
        error={errors.name}
        fullWidth
        required
      />

      <Textarea
        label="Description"
        value={formData.description}
        onChange={(e) => handleChange('description', e.target.value)}
        error={errors.description}
        rows={3}
        fullWidth
      />

      {isEditing && (
        <>
          <Select
            label="Subscription Tier"
            value={formData.subscription_tier || 'FREE'}
            onChange={(value) => handleChange('subscription_tier', value)}
            options={TIER_OPTIONS}
            fullWidth
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Max Users"
              type="number"
              value={formData.max_users?.toString() || '5'}
              onChange={(e) =>
                handleChange('max_users', parseInt(e.target.value) || 5)
              }
              fullWidth
            />

            <Input
              label="Max Projects"
              type="number"
              value={formData.max_projects?.toString() || '10'}
              onChange={(e) =>
                handleChange('max_projects', parseInt(e.target.value) || 10)
              }
              fullWidth
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="is_active" className="text-sm text-secondary-700">
              Organization is active
            </label>
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={loading}>
          {isEditing ? 'Update Organization' : 'Create Organization'}
        </Button>
      </div>
    </form>
  );
};

export default OrganizationForm;
